/**
 * PortalTab — the study's clinician-portal presence (clinician-portal P2 §7,
 * O-12). The portal's study directory is CONFIDENTIAL: it is shown only to
 * verified, signed-in referring clinicians, and listing a study here never
 * publishes anything to the public website — that is WebsiteTab's job, one tab
 * over, and the two toggles are deliberately independent.
 *
 * THE I/E DISCLOSURE LADDER IS THE SENSITIVE PART. 'none' and 'summary' reuse
 * copy that is already public; 'full' publishes the sponsor's own protocol
 * wording (a pinned, CONFIRMED review-criteria set) to external clinicians,
 * which is a sponsor-permission decision — hence the permanent banner, the
 * "where is permission recorded" field, and the audit trail server-side. The
 * portal also WITHHOLDS a pinned set at read time once eReg re-versions the
 * protocol underneath it (source_stale) — stale wording is worse than none.
 *
 * Admin-gated edits (mirrors WebsiteTab); recruiters get a read-only card.
 * All fields PATCH /api/crm/studies/:id — portal fields only.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, ShieldCheck } from 'lucide-react';
import api from '../../../services/api';
import { Study, StudyDetailResponse } from '../../types';
import { Alert } from '../ui';

const inputCls =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';
const labelCls = 'mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400';

type IeDisclosure = 'none' | 'summary' | 'full';

/** The three disclosure levels, with the words that make the choice legible. */
const DISCLOSURES: { value: IeDisclosure; label: string; desc: string }[] = [
  { value: 'none', label: 'None', desc: 'No criteria in the portal' },
  {
    value: 'summary',
    label: 'Summary',
    desc: 'The requirements summary bullets only — what the public site and print one-pager already show',
  },
  { value: 'full', label: 'Full', desc: 'Verbatim protocol criteria from a pinned, confirmed set' },
];

/** The slice of GET /api/crm/studies/:id/review-criteria the pin picker needs. */
interface PinnableCriteriaSet {
  id: string;
  version: number;
  status: string;
  confirmed_at: string | null;
  /** eReg re-versioned a source document since extraction — the portal withholds these at read time. */
  source_stale?: boolean;
}

/** The editable portal fields (strings default '' for controlled inputs). */
interface PortalForm {
  show_on_portal: boolean;
  portal_synopsis: string;
  portal_enrollment_notes: string;
  portal_ie_disclosure: IeDisclosure;
  portal_ie_criteria_set_id: string; // '' = none pinned
  portal_ie_sponsor_ref: string;
  portal_show_sponsor: boolean;
}

function formFromStudy(study: Study): PortalForm {
  const d = study.portal_ie_disclosure;
  return {
    show_on_portal: study.show_on_portal === true,
    portal_synopsis: study.portal_synopsis || '',
    portal_enrollment_notes: study.portal_enrollment_notes || '',
    portal_ie_disclosure: d === 'summary' || d === 'full' ? d : 'none',
    portal_ie_criteria_set_id: study.portal_ie_criteria_set_id || '',
    portal_ie_sponsor_ref: study.portal_ie_sponsor_ref || '',
    portal_show_sponsor: study.portal_show_sponsor === true,
  };
}

/** PATCH payload: trimmed strings -> null; ONLY the portal fields. */
function toPayload(f: PortalForm) {
  const s = (v: string) => (v.trim() ? v.trim() : null);
  return {
    show_on_portal: f.show_on_portal,
    portal_synopsis: s(f.portal_synopsis),
    portal_enrollment_notes: s(f.portal_enrollment_notes),
    portal_ie_disclosure: f.portal_ie_disclosure,
    portal_ie_criteria_set_id: f.portal_ie_criteria_set_id || null,
    portal_ie_sponsor_ref: s(f.portal_ie_sponsor_ref),
    portal_show_sponsor: f.portal_show_sponsor,
  };
}

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

function Toggle({ on, onClick, label }: { on: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={onClick}
      className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
        on ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
      }`}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-150 ${on ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

export default function PortalTab({ detail, isAdmin, onChanged }: { detail: StudyDetailResponse; isAdmin: boolean; onChanged: () => void }) {
  const { study } = detail;
  const [form, setForm] = useState<PortalForm>(() => formFromStudy(study));
  const [baseline, setBaseline] = useState(() => JSON.stringify(toPayload(formFromStudy(study))));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sets, setSets] = useState<PinnableCriteriaSet[] | null>(null);
  const [setsError, setSetsError] = useState<string | null>(null);

  // Re-sync local form when the study reloads (e.g. after another tab's save).
  useEffect(() => {
    const next = formFromStudy(study);
    setForm(next);
    setBaseline(JSON.stringify(toPayload(next)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [study.id, study.updated_at]);

  // The pin picker's sets, fetched lazily the first time 'full' is chosen —
  // only CONFIRMED sets are pinnable (drafts and retired sets never publish).
  useEffect(() => {
    if (!isAdmin || form.portal_ie_disclosure !== 'full' || sets !== null) return undefined;
    let alive = true;
    api.get(`/api/crm/studies/${study.id}/review-criteria`)
      .then((res) => {
        if (!alive) return;
        const confirmed = ((res.data?.sets || []) as PinnableCriteriaSet[]).filter((s) => s.status === 'confirmed');
        setSets(confirmed);
        setSetsError(null);
      })
      .catch((err) => {
        if (alive) setSetsError(err?.response?.data?.error || 'Failed to load criteria sets');
      });
    return () => { alive = false; };
  }, [isAdmin, form.portal_ie_disclosure, sets, study.id]);

  const dirty = useMemo(() => JSON.stringify(toPayload(form)) !== baseline, [form, baseline]);
  const set = <K extends keyof PortalForm>(key: K, value: PortalForm[K]) => setForm((f) => ({ ...f, [key]: value }));

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/api/crm/studies/${study.id}`, toPayload(form));
      setBaseline(JSON.stringify(toPayload(form)));
      onChanged();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to save portal settings');
    } finally {
      setSaving(false);
    }
  }, [study.id, form, onChanged]);

  const selectedSet = sets?.find((s) => s.id === form.portal_ie_criteria_set_id) || null;
  const missingSynopsis = form.show_on_portal && !form.portal_synopsis.trim();

  // ---- Read-only view for recruiters ---------------------------------------
  if (!isAdmin) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
        <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <ShieldCheck className="h-4 w-4 text-primary-600 dark:text-primary-400" /> Clinician portal
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {form.show_on_portal
            ? <>This study is <span className="font-medium text-green-700 dark:text-green-400">listed in the clinician portal</span>.</>
            : <>This study is <span className="font-medium text-gray-500">not listed in the clinician portal</span>.</>}
        </p>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          The portal directory is confidential — it is shown only to verified referring clinicians, never on the public
          website.
        </p>
      </div>
    );
  }

  // ---- Admin editor --------------------------------------------------------
  return (
    <div className="space-y-4">
      {/* Permanent O-12 banner — not dismissible, by design. */}
      <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          Criteria disclosure is a sponsor-permission decision (O-12). Default is closed; turning on
          &lsquo;full&rsquo; publishes the sponsor&apos;s own protocol wording to external clinicians and is audited.
        </span>
      </div>

      {/* Section 1: directory listing + clinician-facing copy */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
              <ShieldCheck className="h-4 w-4 text-primary-600 dark:text-primary-400" /> Show in clinician portal
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              When on, this study appears in the portal&apos;s study directory for referring clinicians. Nothing becomes
              visible to clinicians until this is on; the directory is confidential — listing here does not publish to
              the public site.
            </p>
          </div>
          <Toggle on={form.show_on_portal} onClick={() => set('show_on_portal', !form.show_on_portal)} label="Show in clinician portal" />
        </div>

        {missingSynopsis && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Add a <strong>synopsis</strong> below — the directory entry looks empty without one.</span>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className={labelCls}>Synopsis <span className="text-gray-400">(what referring clinicians see about the study)</span></label>
            <textarea
              rows={4}
              placeholder="A clinician-facing synopsis: condition, phase, what participation involves."
              value={form.portal_synopsis}
              className={inputCls}
              onChange={(e) => set('portal_synopsis', e.target.value)}
            />
          </div>

          <div>
            <label className={labelCls}>Target enrollment / timeline notes — clinician-facing</label>
            <textarea
              rows={2}
              placeholder="e.g. Enrolling through Q4; 12 of 30 slots filled."
              value={form.portal_enrollment_notes}
              className={inputCls}
              onChange={(e) => set('portal_enrollment_notes', e.target.value)}
            />
          </div>

          <div className="flex flex-wrap items-start justify-between gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
            <div className="max-w-prose">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Show sponsor identity</h3>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Sponsor identity is often confidential during recruitment — leave off unless the sponsor has agreed.
              </p>
            </div>
            <Toggle on={form.portal_show_sponsor} onClick={() => set('portal_show_sponsor', !form.portal_show_sponsor)} label="Show sponsor identity" />
          </div>
        </div>
      </div>

      {/* Section 2: I/E criteria disclosure (O-12) */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
        <h2 className="mb-1 text-base font-semibold text-gray-900 dark:text-gray-100">Inclusion/exclusion disclosure</h2>
        <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
          How much of the study&apos;s eligibility criteria referring clinicians may read in the portal.
        </p>

        <div className="space-y-2" role="radiogroup" aria-label="I/E disclosure level">
          {DISCLOSURES.map((d) => (
            <label
              key={d.value}
              className={`flex cursor-pointer items-start gap-2.5 rounded-lg border p-3 transition-colors duration-150 ${
                form.portal_ie_disclosure === d.value
                  ? 'border-primary-300 bg-primary-50/60 dark:border-primary-800 dark:bg-primary-900/10'
                  : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
              }`}
            >
              <input
                type="radio"
                name="portal-ie-disclosure"
                className="mt-0.5 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                checked={form.portal_ie_disclosure === d.value}
                onChange={() => set('portal_ie_disclosure', d.value)}
              />
              <span>
                <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{d.label}</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400">{d.desc}</span>
              </span>
            </label>
          ))}
        </div>

        {form.portal_ie_disclosure === 'full' && (
          <div className="mt-4 space-y-3 border-t border-gray-100 pt-4 dark:border-gray-700">
            <div>
              <label className={labelCls}>Pinned criteria set</label>
              {setsError && <p className="text-sm text-red-600 dark:text-red-400">{setsError}</p>}
              {!setsError && sets === null && (
                <p className="flex items-center gap-2 text-sm text-gray-400">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading confirmed sets…
                </p>
              )}
              {sets !== null && sets.length === 0 && (
                <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-500 dark:bg-gray-900/40 dark:text-gray-400">
                  No confirmed criteria sets yet — extract and confirm one on the Prescreen tab first. Nothing can be
                  disclosed until a set is confirmed and pinned.
                </p>
              )}
              {sets !== null && sets.length > 0 && (
                <div className="space-y-2">
                  {sets.map((s) => (
                    <label
                      key={s.id}
                      className={`flex cursor-pointer items-start gap-2.5 rounded-lg border p-2.5 transition-colors duration-150 ${
                        form.portal_ie_criteria_set_id === s.id
                          ? 'border-primary-300 bg-primary-50/60 dark:border-primary-800 dark:bg-primary-900/10'
                          : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                      }`}
                    >
                      <input
                        type="radio"
                        name="portal-ie-set"
                        className="mt-0.5 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                        checked={form.portal_ie_criteria_set_id === s.id}
                        onChange={() => set('portal_ie_criteria_set_id', s.id)}
                      />
                      <span className="flex flex-wrap items-center gap-1.5 text-sm text-gray-900 dark:text-gray-100">
                        Version {s.version}
                        <span className="text-xs text-gray-500 dark:text-gray-400">confirmed {fmtDate(s.confirmed_at)}</span>
                        {s.source_stale && (
                          <span className="inline-flex rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700 dark:bg-red-900/40 dark:text-red-300">
                            stale — protocol changed in eReg since extraction
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {sets !== null && sets.length > 0 && !form.portal_ie_criteria_set_id && (
                <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                  Pick the set to publish — &lsquo;Full&rsquo; with nothing pinned shows clinicians no criteria.
                </p>
              )}
              {selectedSet?.source_stale && (
                <Alert tone="error" size="sm" icon={false} className="mt-2">
                  This set is stale — the protocol changed in eReg after it was extracted. You can keep it pinned, but
                  the portal WITHHOLDS stale sets at read time: clinicians see no criteria until a fresh set is
                  confirmed and pinned here.
                </Alert>
              )}
            </div>

            <div>
              <label className={labelCls}>Where is the sponsor&apos;s permission recorded? (CDA clause, email date, CTA section)</label>
              <input
                type="text"
                placeholder="e.g. CTA §7.2, or sponsor email 2026-07-14"
                value={form.portal_ie_sponsor_ref}
                className={inputCls}
                onChange={(e) => set('portal_ie_sponsor_ref', e.target.value)}
              />
            </div>

            {study.portal_ie_approved_at && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Approved by <span className="font-mono">{study.portal_ie_approved_by || '—'}</span> on{' '}
                <span className="font-mono">{study.portal_ie_approved_at}</span>
              </p>
            )}
          </div>
        )}
      </div>

      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

      {/* Sticky save */}
      <div className="sticky bottom-0 flex items-center justify-end gap-2 rounded-xl border border-gray-200 bg-white/90 p-3 backdrop-blur dark:border-gray-700 dark:bg-gray-800/90">
        {dirty && <span className="mr-auto text-xs text-amber-600 dark:text-amber-400">Unsaved changes</span>}
        <button
          type="button"
          onClick={() => { const next = formFromStudy(study); setForm(next); }}
          disabled={!dirty || saving}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          Discard
        </button>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : 'Save portal settings'}
        </button>
      </div>
    </div>
  );
}
